(() => {
  if (customElements.get('shunyang-product')) return;

  class ShunyangProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.variants = this.readVariants();
      this.optionInputs = [...this.querySelectorAll('[data-sy-option]')];
      this.variantInput = this.querySelector('[data-sy-variant-id]');
      this.quantityInput = this.querySelector('[data-sy-quantity]');
      this.addButton = this.querySelector('[data-sy-add-button]');
      this.addLabel = this.querySelector('[data-sy-add-label]');
      this.priceNode = this.querySelector('[data-sy-price]');
      this.comparePriceNode = this.querySelector('[data-sy-compare-price]');
      this.availabilityNode = this.querySelector('[data-sy-availability]');
      this.selectedVariantNode = this.querySelector('[data-sy-selected-variant]');
      this.lightbox = this.querySelector('[data-sy-lightbox]');
      this.lightboxImage = this.querySelector('[data-sy-lightbox-image]');
      this.quoteDialog = this.querySelector('[data-sy-quote-dialog]');
      this.wechatDialog = this.querySelector('[data-sy-wechat-dialog]');
      this.activeVariant = this.variantById(this.variantInput?.value) || this.variantFromOptions() || this.variants[0];
      this.lastFocusedElement = null;
      this.handleKeydown = this.onKeydown.bind(this);

      this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
      this.querySelectorAll('[data-sy-static-variant]').forEach((input) => input.addEventListener('change', () => this.selectVariant(input.dataset.syStaticVariant)));
      this.querySelectorAll('[data-sy-media-trigger]').forEach((button) => button.addEventListener('click', () => this.selectMedia(button.dataset.syMediaTrigger)));
      this.querySelectorAll('[data-sy-select-variant]').forEach((button) => button.addEventListener('click', () => this.selectVariant(button.dataset.sySelectVariant, true)));
      this.querySelectorAll('[data-sy-open-quote]').forEach((button) => button.addEventListener('click', () => this.openDialog(this.quoteDialog, button)));
      this.querySelector('[data-sy-contact="wechat"]')?.addEventListener('click', (event) => this.openDialog(this.wechatDialog, event.currentTarget));
      this.querySelector('[data-sy-open-lightbox]')?.addEventListener('click', (event) => this.openLightbox(event.currentTarget));
      this.querySelector('[data-sy-close-lightbox]')?.addEventListener('click', () => this.closeLightbox());
      this.querySelectorAll('[data-sy-close-dialog]').forEach((button) => button.addEventListener('click', () => this.closeDialog(button.closest('[data-sy-quote-dialog], [data-sy-wechat-dialog]'))));
      [this.quoteDialog, this.wechatDialog].forEach((dialog) => dialog?.addEventListener('click', (event) => { if (event.target === dialog) this.closeDialog(dialog); }));
      this.lightbox?.addEventListener('click', (event) => { if (event.target === this.lightbox) this.closeLightbox(); });
      document.addEventListener('keydown', this.handleKeydown);

      if (this.activeVariant) this.syncVariant(this.activeVariant, false);
      if (this.querySelector('.sy-form-status')) this.openDialog(this.quoteDialog);
    }

    disconnectedCallback() { document.removeEventListener('keydown', this.handleKeydown); }

    readVariants() {
      try { return JSON.parse(this.querySelector('[data-sy-variants]')?.textContent || '[]'); }
      catch (error) { console.error('Shunyang product variant data is invalid.', error); return []; }
    }

    variantById(id) { return this.variants.find((variant) => String(variant.id) === String(id)); }
    selectedOptions() { return [...this.querySelectorAll('[data-option-position]')].map((group) => group.querySelector('[data-sy-option]:checked')?.value || ''); }
    variantFromOptions() {
      if (!this.optionInputs.length) return this.variants[0];
      const selected = this.selectedOptions();
      return this.variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
    }
    onOptionChange() { const variant = this.variantFromOptions(); variant ? this.syncVariant(variant, true) : this.setUnavailable(); }

    selectVariant(id, shouldScroll = false) {
      const variant = this.variantById(id);
      if (!variant) return;
      variant.options.forEach((value, index) => {
        const group = this.querySelector(`[data-option-position="${index + 1}"]`);
        const input = [...(group?.querySelectorAll('[data-sy-option]') || [])].find((node) => node.value === value);
        if (input) input.checked = true;
      });
      this.syncVariant(variant, true);
      if (shouldScroll) this.querySelector('[data-sy-variant-picker]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    syncVariant(variant, updateUrl) {
      this.activeVariant = variant;
      if (this.variantInput) { this.variantInput.value = variant.id; this.variantInput.dispatchEvent(new Event('change', { bubbles: true })); }
      if (this.selectedVariantNode) this.selectedVariantNode.textContent = variant.title;
      this.querySelectorAll('[data-sy-spec]').forEach((node) => { node.textContent = variant[node.dataset.sySpec] || '—'; });
      this.querySelectorAll('[data-sy-model-card]').forEach((card) => card.classList.toggle('is-selected', String(card.dataset.syModelCard) === String(variant.id)));
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateQuantityRules(variant);
      this.updateQuoteFields(variant);
      this.updateContactLinks(variant);
      if (variant.mediaId) this.selectMedia(variant.mediaId);
      if (updateUrl) this.updateUrl(variant.id);
      this.dispatchEvent(new CustomEvent('shunyang:variant-change', { bubbles: true, detail: { variant } }));
    }

    setUnavailable() {
      if (this.variantInput) this.variantInput.value = '';
      if (this.addButton) this.addButton.disabled = true;
      if (this.addLabel) this.addLabel.textContent = this.dataset.unavailableText || 'Unavailable';
      if (this.availabilityNode) this.availabilityNode.textContent = this.dataset.unavailableText || 'Unavailable';
    }

    updatePrice(variant) {
      if (!this.priceNode) return;
      this.priceNode.textContent = this.formatMoney(variant.price);
      if (!this.comparePriceNode) return;
      const hasComparePrice = Number(variant.compareAtPrice) > Number(variant.price);
      this.comparePriceNode.hidden = !hasComparePrice;
      this.comparePriceNode.textContent = hasComparePrice ? this.formatMoney(variant.compareAtPrice) : '';
    }

    updateAvailability(variant) {
      if (this.addButton) this.addButton.disabled = !variant.available;
      if (this.addLabel) this.addLabel.textContent = variant.available ? (this.dataset.addToCartText || 'Add to cart') : (this.dataset.soldOutText || 'Sold out');
      if (this.availabilityNode) this.availabilityNode.textContent = variant.available ? (this.dataset.availableText || 'Available to order') : (this.dataset.soldOutText || 'Sold out');
    }

    updateQuantityRules(variant) {
      if (!this.quantityInput) return;
      const minimum = Number(variant.quantityMin || 1);
      const increment = Number(variant.quantityIncrement || 1);
      this.quantityInput.min = String(minimum);
      this.quantityInput.step = String(increment);
      if (variant.quantityMax) this.quantityInput.max = String(variant.quantityMax); else this.quantityInput.removeAttribute('max');
      if (Number(this.quantityInput.value) < minimum) this.quantityInput.value = String(minimum);
    }

    updateQuoteFields(variant) {
      const variantField = this.querySelector('[data-sy-quote-variant]');
      const productText = this.querySelector('[data-sy-dialog-product]');
      const urlField = this.querySelector('[data-sy-quote-url]');
      if (variantField) variantField.value = variant.title;
      if (productText) productText.textContent = `${this.dataset.productTitle} · ${variant.title}`;
      if (urlField) urlField.value = this.variantUrl(variant.id);
    }

    updateContactLinks(variant) {
      const message = `Hello, I am interested in ${this.dataset.productTitle} — ${variant.title}. Please share current pricing, lead time and customization options. ${this.variantUrl(variant.id)}`;
      const whatsapp = this.querySelector('[data-sy-contact="whatsapp"]');
      const email = this.querySelector('[data-sy-contact="email"]');
      const number = (this.dataset.whatsappNumber || '').replace(/[^0-9]/g, '');
      if (whatsapp) whatsapp.href = number ? `https://wa.me/${number}?text=${encodeURIComponent(message)}` : '#';
      if (email) email.href = this.dataset.salesEmail ? `mailto:${this.dataset.salesEmail}?subject=${encodeURIComponent(`Product inquiry: ${this.dataset.productTitle} — ${variant.title}`)}&body=${encodeURIComponent(message)}` : '#';
    }

    selectMedia(id) {
      const target = this.querySelector(`[data-sy-media="${id}"]`);
      if (!target) return;
      this.querySelectorAll('[data-sy-media]').forEach((media) => { const active = media === target; media.hidden = !active; media.classList.toggle('is-active', active); if (!active) media.querySelectorAll('video').forEach((video) => video.pause()); });
      this.querySelectorAll('[data-sy-media-trigger]').forEach((button) => { const active = String(button.dataset.syMediaTrigger) === String(id); button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active)); });
      const zoomButton = this.querySelector('[data-sy-open-lightbox]');
      if (zoomButton) zoomButton.hidden = target.dataset.mediaType !== 'image';
    }

    currentImage() { return this.querySelector('[data-sy-media].is-active img'); }
    openLightbox(trigger) {
      const image = this.currentImage();
      if (!this.lightbox || !this.lightboxImage || !image) return;
      this.lastFocusedElement = trigger;
      this.lightboxImage.src = image.currentSrc || image.src;
      this.lightboxImage.alt = image.alt || this.dataset.productTitle;
      this.lightbox.hidden = false;
      document.body.classList.add('sy-product-modal-open');
      this.lightbox.querySelector('[data-sy-close-lightbox]')?.focus();
    }
    closeLightbox() { if (!this.lightbox) return; this.lightbox.hidden = true; this.unlockBody(); this.restoreFocus(); }
    openDialog(dialog, trigger) { if (!dialog) return; this.lastFocusedElement = trigger || document.activeElement; dialog.hidden = false; document.body.classList.add('sy-product-modal-open'); dialog.querySelector('[data-sy-close-dialog]')?.focus(); }
    closeDialog(dialog) { if (!dialog) return; dialog.hidden = true; this.unlockBody(); this.restoreFocus(); }
    unlockBody() { if (!this.querySelector('.sy-dialog:not([hidden]), .sy-lightbox:not([hidden])')) document.body.classList.remove('sy-product-modal-open'); }
    restoreFocus() { if (this.lastFocusedElement instanceof HTMLElement) this.lastFocusedElement.focus(); this.lastFocusedElement = null; }
    onKeydown(event) { if (event.key !== 'Escape') return; if (this.lightbox && !this.lightbox.hidden) return this.closeLightbox(); const dialog = this.querySelector('.sy-dialog:not([hidden])'); if (dialog) this.closeDialog(dialog); }
    variantUrl(id) { const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin); url.searchParams.set('variant', id); return url.toString(); }
    updateUrl(id) { if (!history.replaceState) return; const url = new URL(window.location.href); url.searchParams.set('variant', id); history.replaceState({}, '', url); }
    formatMoney(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
      const format = this.dataset.moneyFormat || '${{amount}}';
      const token = format.match(/\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator)?)\s*\}\}/)?.[1] || 'amount';
      const noDecimals = token.includes('no_decimals');
      const commaSeparator = token.includes('comma_separator');
      const value = Number(cents || 0) / 100;
      const formatted = value.toLocaleString(commaSeparator ? 'de-DE' : 'en-US', { minimumFractionDigits: noDecimals ? 0 : 2, maximumFractionDigits: noDecimals ? 0 : 2 });
      return format.replace(/\{\{\s*amount[^}]*\}\}/, formatted);
    }
  }

  customElements.define('shunyang-product', ShunyangProduct);
})();
